import {
  useState,
  FocusEvent,
  KeyboardEvent,
  ChangeEvent,
  useEffect,
  useRef,
} from 'react';
import { useHistory } from 'react-router-dom';
import {
  MarketButton,
  MarketHeader,
  MarketInputText,
  MarketField,
} from '@market/react';
import { useDispatch, useSelector } from 'react-redux';
import { ReactComponent as ArrowLeft } from '@market/icons/assets/svg/arrow-left.svg';
import { ReactComponent as CheckmarkCircle } from '@market/icons/assets/svg/checkmark-circle.svg';
import {
  enrollCustomer,
  enrollCustomerStateReset,
  enrollCustomerStateSelector,
} from 'store/customer';
import ConnectionError from './ConnectionError';
import { EnrollmentErrorCode, SquareLoyaltyApiError } from 'store/api/errors';
import { useAutoFocus } from 'hooks/useAutoFocus';
import {
  formatPhoneNumber,
  phoneNumberIsValid,
  unformatPhoneNumber,
} from 'utils/phoneNumber';

type ErrorSubtextState = {
  show: boolean;
  text: string;
};

const PHONE_NUMBER_INPUT_ALLOWED_KEY_PRESSES = new Set([
  '0',
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  'Backspace',
  'Delete',
  'ArrowLeft',
  'ArrowRight',
]);

const SendEnrollmentLinkScreen = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const autoFocus = useAutoFocus();

  const firstRender = useRef(true);

  const [formattedPhoneNumber, setFormattedPhoneNumber] = useState('');
  const enrollCustomerState = useSelector(enrollCustomerStateSelector);
  const [errorSubtext, setErrorSubtext] = useState({
    show: false,
    text: '',
  } as ErrorSubtextState);
  const [showConnectionError, setShowConnectionError] = useState(false);

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
    }
  }, []);

  useEffect(() => {
    const error = enrollCustomerState.error;

    if (error instanceof SquareLoyaltyApiError) {
      switch (error.code) {
        case EnrollmentErrorCode.DUPLICATE_PHONE_NUMBER:
          setErrorSubtext({
            show: true,
            text: 'Phone number already enrolled.',
          });
          return;
        case EnrollmentErrorCode.INVALID_PHONE_NUMBER:
          setErrorSubtext({
            show: true,
            text: 'Please enter a valid phone number.',
          });
          return;
        default:
          setShowConnectionError(true);
          return;
      }
    } else if (error) {
      setShowConnectionError(true);
    }
  }, [enrollCustomerState.error]);

  const onClickSendButton = () => {
    const unformattedPhoneNumber = unformatPhoneNumber(formattedPhoneNumber);
    if (phoneNumberIsValid(unformattedPhoneNumber)) {
      dispatch(enrollCustomer(unformattedPhoneNumber));
    } else {
      setErrorSubtext({
        show: true,
        text: 'Please enter a valid phone number.',
      });
    }
  };

  const onPhoneNumberInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setErrorSubtext({
      show: false,
      text: '',
    });
    setFormattedPhoneNumber(event.target.value);
  };

  const onPhoneNumberInputBlur = (event: FocusEvent<HTMLInputElement>) => {
    setFormattedPhoneNumber(formatPhoneNumber(formattedPhoneNumber));
  };

  const onPhoneNumberInputKeyDown = (
    event: KeyboardEvent<HTMLInputElement>
  ) => {
    if (PHONE_NUMBER_INPUT_ALLOWED_KEY_PRESSES.has(event.key)) {
      return;
    }

    if (event.key === 'Enter' && !enrollCustomerState.isPending) {
      (document.activeElement as HTMLElement)?.blur();
      onClickSendButton();
    }

    event.preventDefault();
  };

  const returnToMainScreen = () => {
    dispatch(enrollCustomerStateReset());
    history.goBack();
  };

  if (enrollCustomerState.apiSuccess) {
    return (
      <div className="addons-full-width-and-height addons-center-items addons-modal-flex-direction addons-direct-child-spacing">
        <CheckmarkCircle
          className="addons-checkmark-circle-icon"
          width="80"
          height="80"
        />
        <div className="addons-text-align-center">
          <strong>Enrollment link sent</strong>
          <p style={{ margin: '10px 0 0 0' }}>{formattedPhoneNumber}</p>
        </div>
        <MarketButton
          data-testid="send-enrollment-link-done-btn"
          onClick={returnToMainScreen}
          rank="primary"
          slot="actions"
        >
          Done
        </MarketButton>
      </div>
    );
  }

  if (showConnectionError) {
    return (
      <ConnectionError
        onClickTryAgain={() => {
          setShowConnectionError(false);
          dispatch(enrollCustomer(unformatPhoneNumber(formattedPhoneNumber)));
        }}
      />
    );
  }

  return (
    <div className="market-grid-container">
      <MarketHeader className="market-grid-item-full addons-header" compact>
        <MarketButton
          data-testid="send-enrollment-link-back-btn"
          onClick={returnToMainScreen}
          rank="secondary"
          slot="navigation"
        >
          <div slot="icon" style={{ lineHeight: 'normal' }}>
            <ArrowLeft className="addons-back-icon" width="24" height="24" />
          </div>
        </MarketButton>
        <h2>Send enrollment link</h2>
        <MarketButton
          data-testid="send-enrollment-link-send-btn"
          isLoading={enrollCustomerState.isPending || undefined}
          onClick={onClickSendButton}
          rank="primary"
          slot="actions"
        >
          Send
        </MarketButton>
      </MarketHeader>

      <MarketField
        className="market-grid-item-full"
        data-testid="send-enrollment-link-phone-number-field"
        invalid={errorSubtext.show}
      >
        {/* TODO: This is a temporary solution. Replace this with Market
        component's phone number input once it's been implemented. */}
        <MarketInputText
          ref={(ref) => {
            if (firstRender.current) {
              autoFocus(ref);
            }
          }}
          placeholder="(000) 000-0000"
          className="market-grid-item-full"
          autoCorrect="off"
          autoCapitalize="none"
        >
          <label>Phone number</label>
          <input
            data-testid="send-enrollment-link-phone-number-input"
            slot="input"
            value={formattedPhoneNumber}
            onChange={onPhoneNumberInputChange}
            onBlur={onPhoneNumberInputBlur}
            onKeyDown={onPhoneNumberInputKeyDown}
            type="text"
          />
        </MarketInputText>
        <small slot="helper" className="addons-helper-slot-text">
          They will need to complete enrollment before they can start earning
          points.
        </small>
        <small slot="error">
          {errorSubtext.text}
          <div className="addons-helper-slot-text">
            They will need to complete enrollment before they can start earning
            points.
          </div>
        </small>
      </MarketField>
    </div>
  );
};

export default SendEnrollmentLinkScreen;
